import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getChargeAuthorizationURL,
  getMerchantAuthentication
} from "../pages/DefaultPages/common/merchantAuthenticationByCounty";
import axios from "axios";
import { RootState } from "./index";

export enum ChargeRequestStatus {
  NONE = 'none',
  PENDING = 'pending',
  APPROVED = 'approved',
  DECLINED = 'declined',
  FAILED = 'failed',
}

export enum ChargeResponseCode {
  APPROVED = "1",
  DECLINED = "2",
  ERROR = "3",
  HELD_FOR_REVIEW = "4",
}


type ChargeResponse =
  {
    refId?: string,
    transactionResponse: {
      responseCode: ChargeResponseCode,
      authCode: string,
      avsResultCode: string,
      cvvResultCode: string,
      cavvResultCode: string,
      transId: string,
      refTransID: string,
      transHash: string,
      testRequest: string,
      accountNumber: string,
      accountType: string,
      messages: [
        {
          code: string,
          description: string
        }
      ],
      transHashSha2: string,
      SupplementalDataQualificationIndicator: number,
      networkTransId: string
    },
    messages: {
      resultCode: "Ok" | "Error",
      message: [
        {
          code: string,
          text: string
        }
      ]
    }
  }

interface CardState {
  cardNumber: string | null;
  expiration: string | null;
  cvc: string | null;
  cardHolderName: string | null;
}

interface CreditCardState {
  card: CardState;
  chargeStatus: ChargeRequestStatus;
  chargeError?: string;
  chargeResponse?: ChargeResponse;
}

const initialCardState: CardState = {
  cardNumber: null,
  expiration: null,
  cvc: null,
  cardHolderName: null,
};

const initialState: CreditCardState = {
  card: initialCardState,
  chargeStatus: ChargeRequestStatus.NONE
}

export const wasChargeApproved = (response: ChargeResponse): boolean => response.messages?.resultCode === "Ok" &&
  !!response.transactionResponse?.authCode && response.transactionResponse?.messages &&
  response.transactionResponse?.messages.length > 0 &&
  response.transactionResponse?.messages[0].code === "1";

export const chargeCreditCard = createAsyncThunk<
  ChargeResponse,
  { countyKey: string, amount: number },
  { state: RootState }
>(
  "creditCard/charge",
  async ({ countyKey, amount }, { getState }) => {
    const merchantAuthentication = getMerchantAuthentication(countyKey);
    const state = getState();
    const cardNumber = selectCreditCardNumber(state);
    const payment = {
      creditCard: {
        cardNumber,
        expirationDate: selectCreditCardExpiration(state),
        cardCode: selectCreditCardCvc(state)
      }
    };
    const response = await axios.post(getChargeAuthorizationURL(countyKey), {
      createTransactionRequest: {
        merchantAuthentication: merchantAuthentication,
        refId: Math.floor(Math.random() * (9999 - 1000 + 1) + 1000),
        transactionRequest: {
          transactionType: "authCaptureTransaction",
          amount: amount,
          payment,
          transactionSettings: {
            setting: {
              settingName: "duplicateWindow",
              settingValue: 0
            }
          }
        }
      }
    });
    return response.data;
  }
);

export const creditCardSlice = createSlice({
  name: 'creditCard',
  initialState: initialState,
  reducers: {
    setCreditCardNumber: (state, action: PayloadAction<string>) => {
      state.card.cardNumber = action.payload;
      state.chargeStatus = ChargeRequestStatus.NONE;
      state.chargeError = undefined;
    },
    setCreditCardExpiration: (state, action: PayloadAction<string>) => {
      state.card.expiration = action.payload;
      state.chargeStatus = ChargeRequestStatus.NONE;
      state.chargeError = undefined;
    },
    setCreditCardCvc: (state, action: PayloadAction<string>) => {
      state.card.cvc = action.payload;
      state.chargeStatus = ChargeRequestStatus.NONE;
      state.chargeError = undefined;
    },
    setCreditCardHolderName: (state, action: PayloadAction<string>) => {
      state.card.cardHolderName = action.payload;
      state.chargeStatus = ChargeRequestStatus.NONE;
      state.chargeError = undefined;
    },
    setChargeFailure: (state, action) => {
      state.chargeStatus = ChargeRequestStatus.FAILED;
      state.chargeError = action.payload;
    },
    clearCreditCard: (state) => {
      state.card = initialCardState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(chargeCreditCard.pending, (state) => {
        state.chargeStatus = ChargeRequestStatus.PENDING;
        state.chargeError = undefined;
      })
      .addCase(chargeCreditCard.fulfilled, (state, action) => {
        const chargeStatus = wasChargeApproved(action.payload) ? ChargeRequestStatus.APPROVED : ChargeRequestStatus.DECLINED;
        state.chargeStatus = chargeStatus;
        state.chargeResponse = action.payload;
        if (chargeStatus === ChargeRequestStatus.APPROVED) {
          state.card = initialCardState;
        }
      })
      .addCase(chargeCreditCard.rejected, (state, action) => {
        state.chargeError = action.error.message;
        state.chargeStatus = ChargeRequestStatus.FAILED;
      });
  }
});

export const selectCreditCard = (state: RootState) => state.creditCard.card;
export const selectCreditCardNumber = (state: RootState) => state.creditCard.card.cardNumber;
export const selectCreditCardExpiration = (state: RootState) => state.creditCard.card.expiration;
export const selectCreditCardCvc = (state: RootState) => state.creditCard.card.cvc;
export const selectCreditCardHolderName = (state: RootState) => state.creditCard.card.cardHolderName;
export const selectChargeStatus = (state: RootState) => state.creditCard.chargeStatus;
export const selectChargeError = (state: RootState) => state.creditCard.chargeError;

export const { setCreditCardNumber, setCreditCardExpiration, setCreditCardCvc, setCreditCardHolderName,
  setChargeFailure, clearCreditCard } = creditCardSlice.actions;

export default creditCardSlice.reducer;